import dayjs from "dayjs";
import { PredictionErrorBand, predictionErrorBand } from "../reporting/reports";
import type { IPredictionEventSeries } from "../reporting/chart/usePredictionSeries";

/** Days either side of today the page covers. */
export const WINDOW_DAYS = 31;

const YMD = "YYYY-MM-DD";

export interface IProjectionWindow {
    from: string;
    to: string;
    monthFrom: string;
    monthTo: string;
}

export const projectionWindow = (): IProjectionWindow => ({
    from: dayjs().subtract(WINDOW_DAYS, "day").format(YMD),
    to: dayjs().add(WINDOW_DAYS, "day").format(YMD),
    // The calendar month in progress, which a monthly budget is paced against; always inside the forecast horizon.
    monthFrom: dayjs().startOf("month").format(YMD),
    monthTo: dayjs().endOf("month").format(YMD),
});

export interface IEventSummary {
    event: string;
    label: string;
    total: number;
    counted: number;
    stillToLand: number;
    forecastTotal: number;
    forecastDays: number;
    monthTotal: number;
    errorPct: number | null;
    band: PredictionErrorBand | null;
    /** Share of the total not yet counted: still settling plus forecast. */
    uncountedShare: number;
}

/** One headline per event over the window on screen; every figure names the period it covers. */
export const summarizeEvent = (one: IPredictionEventSeries, label: string, win: IProjectionWindow): IEventSummary => {
    const points = one.points.filter((p) => p.pixelDate >= win.from && p.pixelDate <= win.to);
    const forecast = points.filter((p) => p.basis === "forecast");
    const open = points.filter((p) => p.basis === "projected");
    const settled = points.filter((p) => p.basis === "actual");

    const forecastTotal = forecast.reduce((sum, p) => sum + (p.expectedTotal || 0), 0);
    const stillToLand = open.reduce((sum, p) => sum + Math.max(0, (p.expectedTotal || 0) - (p.soFar || 0)), 0);
    const counted = settled.reduce((sum, p) => sum + (p.expectedTotal || 0), 0) + open.reduce((sum, p) => sum + (p.soFar || 0), 0);

    const total = counted + stillToLand + forecastTotal;
    // Summed, not in quadrature, matching the backend: consecutive dates share a curve and a prior.
    const error = points.reduce((sum, p) => sum + (p.standardError || 0), 0);
    const errorPct = total > 0 ? (error / total) * 100 : null;

    return {
        event: one.event,
        label,
        total,
        counted,
        stillToLand,
        forecastTotal,
        forecastDays: forecast.length,
        monthTotal: one.points.filter((p) => p.pixelDate >= win.monthFrom && p.pixelDate <= win.monthTo).reduce((sum, p) => sum + (p.expectedTotal || 0), 0),
        errorPct,
        band: errorPct === null ? null : predictionErrorBand(errorPct),
        uncountedShare: total > 0 ? (stillToLand + forecastTotal) / total : 0,
    };
};

export const formatProjected = (value: number): string =>
    value >= 1000 ? Math.round(value).toLocaleString("en-US") : value.toFixed(value >= 10 ? 0 : 1);
